/**
 * Overview.jsx — Vista geral: 4 KPIs, pipeline, alertas e carga por account.
 */

import React, { useEffect, useState } from "react";
import { fetchOverview, fetchMarcasSaude } from "../services/api";

const BUCKETS_ORDEM = [
  "ROADMAP", "NEGOCIAR DEADLINE", "ONGOING",
  "AGUARDO FEEDBACK", "PARA APROVAÇÃO INTERNA",
  "PARA APROVAÇÃO EXTERNA", "APROVADO", "CONCLUÍDO",
];

export default function Overview({ onNavigateMarca }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [erro, setErro]           = useState(null);
  const [marcasRisco, setMarcasRisco] = useState([]);

  useEffect(() => {
    fetchOverview()
      .then(setData)
      .catch((e) => setErro(e.message))
      .finally(() => setLoading(false));

    fetchMarcasSaude()
      .then((lista) => setMarcasRisco(lista.filter((m) => m.score < 50)))
      .catch(() => {});
  }, []);

  if (loading) return <Loading />;
  if (erro) return <Erro mensagem={erro} />;

  const { kpis, pipeline, ranking_planners } = data;
  const maxAtivas = ranking_planners.length > 0 ? ranking_planners[0].total_ativas : 1;

  return (
    <div>
      <h1 style={styles.titulo}>Overview</h1>

      {/* KPIs */}
      <section className="grid-kpi-4" style={styles.kpiGrid}>
        <KpiCard
          label="Deadline Ultrapassado"
          valor={kpis.tarefas_ultrapassadas}
          cor="#dc2626"
          descricao="Deadline já passou, não concluídas"
        />
        <KpiCard
          label="Deadline Próximo"
          valor={kpis.tarefas_deadline_proximo}
          cor="#f97316"
          descricao="Hoje, amanhã ou depois de amanhã"
        />
        <KpiCard
          label={`Aprov. Externa > ${kpis.aguardo_threshold_dias}d`}
          valor={kpis.aguardo_mais_dias}
          cor="#f97316"
          descricao="Para Aprovação Externa há muito tempo"
        />
        <KpiCard
          label={`Aprov. Interna > ${kpis.aguardo_threshold_dias}d`}
          valor={kpis.aguardo_aprov_interna}
          cor="#eab308"
          descricao="Para Aprovação Interna há muito tempo"
        />
      </section>

      {/* Pipeline */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitulo}>Pipeline</h2>
        <div className="grid-pipeline" style={styles.pipelineGrid}>
          {BUCKETS_ORDEM.map((bucket) => (
            <div key={bucket} style={styles.bucketCard}>
              <span style={styles.bucketNome}>{bucket}</span>
              <span style={styles.bucketTotal}>{pipeline[bucket] ?? 0}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Marcas em alerta */}
      {marcasRisco.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitulo}>
            Marcas em Alerta
            <span style={styles.alertaContagem}>{marcasRisco.length} com score &lt; 50</span>
          </h2>
          <div style={styles.marcasGrid}>
            {marcasRisco.map((m) => (
              <button
                key={m.plan_id}
                style={{
                  ...styles.marcaCard,
                  borderLeftColor: m.score < 50 ? "#dc2626" : "#f97316",
                }}
                onClick={() => onNavigateMarca && onNavigateMarca(m.plan_id)}
              >
                <span style={styles.marcaScore}>
                  <span style={{ color: m.score < 50 ? "#dc2626" : "#f97316", fontWeight: 700 }}>
                    {m.score}
                  </span>
                  <span style={styles.marcaScoreLabel}>score</span>
                </span>
                <span style={styles.marcaCardNome}>{m.plan_title}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Ranking de planners */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitulo}>Planners por Volume de Trabalho</h2>
        {ranking_planners.length === 0 ? (
          <p style={styles.semDados}>Sem dados.</p>
        ) : (
          <div className="table-scroll">
          <table style={styles.tabela}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 32, textAlign: "center" }}>#</th>
                <th style={styles.th}>Planner</th>
                <th style={styles.th}>Project Manager</th>
                <th style={styles.th}>Tarefas ativas</th>
                <th style={{ ...styles.th, width: 110, textAlign: "center" }}>Ultrapassadas</th>
                <th style={{ ...styles.th, width: 110, textAlign: "center" }}>Deadline próximo</th>
              </tr>
            </thead>
            <tbody>
              {ranking_planners.map((p, i) => (
                <tr key={p.plan_id} style={styles.tr}>
                  <td style={{ ...styles.td, textAlign: "center", color: "#94a3b8", fontWeight: 600 }}>
                    {i + 1}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.plannerNome}>{p.plan_title}</span>
                  </td>
                  <td style={{ ...styles.td, color: "#64748b" }}>
                    {p.pm_nome ?? <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.barraWrap}>
                      <div style={{
                        ...styles.barra,
                        width: `${Math.round((p.total_ativas / maxAtivas) * 100)}%`,
                      }} />
                      <span style={styles.barraValor}>{p.total_ativas}</span>
                    </div>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    {p.ultrapassadas > 0
                      ? <span style={styles.badgeRed}>{p.ultrapassadas}</span>
                      : <span style={styles.ok}>—</span>}
                  </td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    {p.deadline_proximo > 0
                      ? <span style={styles.badgeOrange}>{p.deadline_proximo}</span>
                      : <span style={styles.ok}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  );
}

function KpiCard({ label, valor, cor, descricao }) {
  return (
    <div style={{ ...styles.kpiCard, borderTop: `4px solid ${cor}` }}>
      <span style={{ ...styles.kpiValor, color: cor }}>{valor}</span>
      <span style={styles.kpiLabel}>{label}</span>
      <span style={styles.kpiDesc}>{descricao}</span>
    </div>
  );
}

function Loading() {
  return <p style={{ color: "#64748b", marginTop: 40 }}>A carregar dados…</p>;
}

function Erro({ mensagem }) {
  return (
    <div style={styles.erro}>
      <strong>Erro ao carregar dados:</strong> {mensagem}
    </div>
  );
}

const styles = {
  titulo: { fontSize: 24, fontWeight: 700, marginBottom: 24, color: "#1e293b" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 },
  kpiCard: {
    background: "#fff", borderRadius: 8, padding: "20px 24px",
    boxShadow: "0 1px 3px rgba(0,0,0,.08)", display: "flex",
    flexDirection: "column", gap: 4,
  },
  kpiValor: { fontSize: 36, fontWeight: 700, lineHeight: 1 },
  kpiLabel: { fontSize: 14, fontWeight: 600, color: "#374151", marginTop: 4 },
  kpiDesc: { fontSize: 12, color: "#94a3b8" },
  section: { background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,.08)", marginBottom: 24 },
  sectionTitulo: { fontSize: 16, fontWeight: 600, margin: "0 0 16px", color: "#1e293b" },
  pipelineGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },
  bucketCard: {
    border: "1px solid #e2e8f0", borderRadius: 6, padding: "12px 16px",
    display: "flex", flexDirection: "column", gap: 4,
  },
  bucketNome: { fontSize: 12, color: "#64748b" },
  bucketTotal: { fontSize: 22, fontWeight: 700, color: "#1e293b" },
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 12px", backgroundColor: "#f8fafc", fontWeight: 600, borderBottom: "2px solid #e2e8f0", color: "#475569" },
  tr: { borderBottom: "1px solid #f1f5f9" },
  td: { padding: "8px 12px", color: "#374151", verticalAlign: "middle" },
  plannerNome: { fontWeight: 500 },
  barraWrap: { display: "flex", alignItems: "center", gap: 8 },
  barra: { height: 8, backgroundColor: "#6366f1", borderRadius: 4, minWidth: 2, maxWidth: 180, transition: "width 0.3s" },
  barraValor: { fontSize: 13, fontWeight: 700, color: "#374151", minWidth: 28 },
  badgeRed: { display: "inline-block", background: "#fee2e2", color: "#dc2626", borderRadius: 10, padding: "2px 10px", fontSize: 12, fontWeight: 700 },
  badgeOrange: { display: "inline-block", background: "#ffedd5", color: "#ea580c", borderRadius: 10, padding: "2px 10px", fontSize: 12, fontWeight: 700 },
  ok: { color: "#cbd5e1" },
  alertaContagem: { marginLeft: 10, fontSize: 12, fontWeight: 500, color: "#f97316", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "2px 10px" },
  marcasGrid: { display: "flex", flexWrap: "wrap", gap: 8 },
  marcaCard: {
    display: "flex", alignItems: "center", gap: 10,
    background: "#fff", border: "1px solid #e2e8f0", borderLeft: "4px solid",
    borderRadius: 6, padding: "10px 14px", cursor: "pointer",
    textAlign: "left", transition: "box-shadow 0.15s",
    minWidth: 180,
  },
  marcaScore: { display: "flex", flexDirection: "column", alignItems: "center", minWidth: 34 },
  marcaScoreLabel: { fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" },
  marcaCardNome: { fontSize: 13, fontWeight: 500, color: "#374151" },
  semDados: { color: "#94a3b8", fontSize: 14, margin: 0 },
  erro: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 16, color: "#dc2626", marginTop: 24 },
};
